import { formatNumber, formatPercent } from "../../components/charts/formatters"
import { socialNetworkTypeIdToLabel } from "./inboxHealthReport"
import {
  catalogTypeIdFromKey,
  formatFollowerGrowthPercent,
  PULSE_PLATFORM_ICON_PATHS,
  socialNetworkTypeIdToDashboardPlatformKey,
  socialPerformancePlatformsToBreakdown,
} from "./pulseCardReport"
import { formatLocalDateKey } from "../../utils/dateRangeUtils"
import {
  DASHBOARD_SOCIAL_PERFORMANCE_PLATFORM_KEYS,
  INSTAGRAM_GRAPH_DESCRIPTION,
  dashboardPlatformKeyFromDescription,
  socialNetworkDescription,
  socialNetworkPrettyLabelForDashboardKey,
} from "./socialNetworkCatalog"

/** ISO date key used to pick the matching month from GET …/page?period=month */
export function formatSocialPerformanceDate(date) {
  return formatLocalDateKey(date)
}

export function parseSocialPerformanceDateParts(date) {
  const iso = formatSocialPerformanceDate(date)
  if (!iso) return null
  const [year, month, day] = iso.split("-")
  if (!year || !month || !day) return null
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
  }
}

/** All / Organic / Paid filter → page `aggregation_mode`. */
export const TABLE_SOURCE_TO_AGGREGATION_MODE = {
  all: "paid_and_organic",
  organic: "organic_only",
  paid: "paid_only",
}

export function tableSourceToAggregationMode(source) {
  return TABLE_SOURCE_TO_AGGREGATION_MODE[source] ?? TABLE_SOURCE_TO_AGGREGATION_MODE.all
}

/** Query for GET /report/clients/{id}/page (month, day, or custom start_date/end_date). */
export function buildClientPageReportQuery({
  period,
  year,
  month,
  day,
  aggregationMode,
  startDate,
  endDate,
}) {
  const params = {
    aggregation_mode: aggregationMode ?? TABLE_SOURCE_TO_AGGREGATION_MODE.all,
  }
  if (period === "custom" && startDate && endDate) {
    params.start_date = startDate
    params.end_date = endDate
    return new URLSearchParams(params).toString()
  }
  params.period = period
  params.year = String(year)
  params.month = String(month)
  if (period === "day") {
    params.day = String(day)
  }
  return new URLSearchParams(params).toString()
}

export function buildSocialPerformanceFetchKey(
  clientId,
  startDate,
  endDate,
  aggregationMode,
  rangeMode,
) {
  const mode = aggregationMode ?? TABLE_SOURCE_TO_AGGREGATION_MODE.all
  const range = rangeMode === "custom" ? "|custom" : ""
  return `${String(clientId ?? "")}|${startDate ?? ""}|${endDate ?? ""}|${mode}${range}`
}

export function aggregationModeFromSocialFetchKey(fetchKey) {
  const mode = String(fetchKey ?? "").split("|")[3] ?? ""
  return mode || TABLE_SOURCE_TO_AGGREGATION_MODE.all
}

/** Distinct cache key for Soho Pulse daily status snapshot (`startDate` only). */
export function buildPulseSocialPerformanceFetchKey(clientId, startDate) {
  return `pulse-status|${String(clientId ?? "")}|${startDate ?? ""}`
}

/** Dashboard filter keys use `twitter`; some APIs use `x`. */
export function normalizeDashboardPlatformKey(platformKey) {
  if (platformKey == null) return platformKey
  const key = String(platformKey).toLowerCase()
  return key === "x" ? "twitter" : key
}

export function socialNetworkNameToTypeId(name, socialNetworkTypes = []) {
  return catalogTypeIdFromKey(socialNetworkTypes, name)
}

/** Keep only API platform entries that match selected dashboard platform keys. */
export function filterSocialPerformancePlatformsByKeys(
  platforms,
  platformKeysFilter,
  socialNetworkTypes = [],
) {
  if (!platformKeysFilter?.length) return platforms
  const filterIds = new Set(
    platformKeysFilter
      .map((key) => catalogTypeIdFromKey(socialNetworkTypes, key))
      .filter((id) => id != null && id !== 0),
  )
  return (Array.isArray(platforms) ? platforms : []).filter((platform) => {
    const typeId = Number(platform?.social_network_type_id ?? platform?.socialNetworkTypeId)
    if (Number.isNaN(typeId) || typeId === 0) return false
    return filterIds.has(typeId)
  })
}

function platformTypeId(platform) {
  return Number(platform?.social_network_type_id ?? platform?.socialNetworkTypeId)
}

function isApiAllNetwork(networkName) {
  return String(networkName ?? "").trim().toLowerCase() === "all"
}

function findApiAllPlatform(list) {
  return (Array.isArray(list) ? list : []).find((platform) => {
    if (isApiAllNetwork(platform?.social_network ?? platform?.socialNetwork)) return true
    return platformTypeId(platform) === 0
  }) ?? null
}

function pulseDashboardPlatformKey(typeId, socialNetworkTypes) {
  const platformKey = socialNetworkTypeIdToDashboardPlatformKey(typeId, socialNetworkTypes)
  if (!platformKey || !PULSE_PLATFORM_ICON_PATHS[platformKey]) return null
  return platformKey
}

function pulseDashboardPlatformLabel(typeId, platformKey, socialNetworkTypes) {
  return (
    socialNetworkTypeIdToLabel(socialNetworkTypes, typeId) ||
    socialNetworkPrettyLabelForDashboardKey(platformKey)
  )
}

function buildPlatformEngagementRows(platforms, socialNetworkTypes = []) {
  const byKey = new Map()

  for (const platform of Array.isArray(platforms) ? platforms : []) {
    const typeId = platformTypeId(platform)
    if (Number.isNaN(typeId) || typeId === 0) continue
    const platformKey = pulseDashboardPlatformKey(typeId, socialNetworkTypes)
    const label = pulseDashboardPlatformLabel(typeId, platformKey, socialNetworkTypes)
    if (!platformKey || !label) continue

    const engagement = extractPaidOrganicMetric(platform?.metrics ?? {}, "new_post_engagement")
    const totalEngagements = engagement.newValue ?? 0
    const dayDelta = (engagement.newValue ?? 0) - (engagement.previousValue ?? 0)
    const existing = byKey.get(platformKey)
    if (existing) {
      existing.totalEngagements += totalEngagements
      existing.dayDelta += dayDelta
      continue
    }
    byKey.set(platformKey, {
      platform: platformKey,
      label,
      icon: PULSE_PLATFORM_ICON_PATHS[platformKey],
      totalEngagements,
      dayDelta,
    })
  }

  return Array.from(byKey.values()).sort((a, b) => b.totalEngagements - a.totalEngagements)
}

function buildPlatformFollowerRows(platforms, socialNetworkTypes = []) {
  const byKey = new Map()

  for (const platform of Array.isArray(platforms) ? platforms : []) {
    const typeId = platformTypeId(platform)
    if (Number.isNaN(typeId) || typeId === 0) continue
    const platformKey = pulseDashboardPlatformKey(typeId, socialNetworkTypes)
    const label = pulseDashboardPlatformLabel(typeId, platformKey, socialNetworkTypes)
    if (!platformKey || !label) continue

    const followers = extractFollowerMetric(platform?.metrics ?? {})
    const netNew = followers.newValue ?? 0
    const totalFollowers = followers.latestValue ?? 0
    const growth = followers.growth == null ? 0 : Number(followers.growth)
    const existing = byKey.get(platformKey)
    if (existing) {
      existing.totalFollowers += totalFollowers
      existing.netNew += netNew
      continue
    }
    byKey.set(platformKey, {
      platform: platformKey,
      label,
      icon: PULSE_PLATFORM_ICON_PATHS[platformKey],
      totalFollowers,
      netNew,
      growth: Number.isFinite(growth) ? growth : 0,
    })
  }

  return Array.from(byKey.values()).sort((a, b) => (b.growth ?? 0) - (a.growth ?? 0))
}

function extractFollowerMetric(metrics) {
  return extractPaidOrganicMetric(metrics, "followers")
}

export const MONTHLY_SOCIAL_COLUMNS = [
  {
    key: "engagements",
    label: "Engagements",
    apiMetricKey: "new_post_engagement",
    info: "Likes, comments, shares, saves, and other audience actions on your content.",
  },
  {
    key: "engagementRate",
    label: "Engagement Rate",
    apiMetricKey: "new_engagement_rate",
    isRate: true,
    info: "Calculated as engagements divided by impressions for the selected period.",
  },
  { key: "impressions", label: "Impressions", apiMetricKey: "impressions" },
  { key: "reach", label: "Reach", apiMetricKey: "reach" },
  {
    key: "linkClicks",
    label: "Link Clicks",
    apiMetricKey: "link_clicks",
    info: "Made up of post and ad link clicks.",
  },
  {
    key: "followers",
    label: "New Followers",
    apiMetricKey: "followers",
    info: "Net new followers, after accounting for followers gained and lost.",
  },
  { key: "saves", label: "Saves", apiMetricKey: "post_saves" },
  { key: "videoViews", label: "Video Views", apiMetricKey: "post_video_views" },
  { key: "shares", label: "Shares", apiMetricKey: "post_shares" },
]

export function unwrapSocialPerformancePlatforms(data) {
  if (Array.isArray(data?.platforms)) return data.platforms
  if (Array.isArray(data?.data?.platforms)) return data.data.platforms
  if (Array.isArray(data)) return data
  return []
}

function isDailyStatusMetricObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  return (
    "latest_value" in value ||
    "latestValue" in value ||
    "new_value" in value ||
    "newValue" in value ||
    "metric_name" in value ||
    "metricName" in value
  )
}

function collectDailyStatusResults(platform) {
  const results = {}
  if (!platform || typeof platform !== "object") return results
  for (const [key, value] of Object.entries(platform)) {
    if (
      key === "social_network_type_id" ||
      key === "socialNetworkTypeId" ||
      key === "metrics" ||
      key === "social_network" ||
      key === "socialNetwork"
    ) {
      continue
    }
    if (isDailyStatusMetricObject(value)) results[key] = value
  }
  return results
}

function isDailyStatusPayload(data) {
  const platforms = unwrapSocialPerformancePlatforms(data)
  if (!platforms.length) return false
  return platforms.some((platform) => {
    if (!platform || typeof platform !== "object") return false
    const nested = platform.metrics
    if (nested && typeof nested === "object" && Object.keys(nested).length > 0) return false
    return Boolean(
      isDailyStatusMetricObject(platform.followers) ||
        isDailyStatusMetricObject(platform.new_post_engagement),
    )
  })
}

/** GET daily/status platforms have metrics as siblings; nest them as paid_and_organic. */
export function normalizeDailyStatusPayload(data) {
  return unwrapSocialPerformancePlatforms(data).map((platform) => {
    const typeId = Number(platform?.social_network_type_id ?? platform?.socialNetworkTypeId)
    return {
      social_network_type_id: Number.isNaN(typeId) ? platform?.social_network_type_id : typeId,
      socialNetworkTypeId: Number.isNaN(typeId) ? platform?.socialNetworkTypeId : typeId,
      social_network: platform?.social_network ?? platform?.socialNetwork ?? null,
      metrics: pagePerformanceResultsToMetrics(collectDailyStatusResults(platform)),
    }
  })
}

function aggregationModeToMetricsBucketKey(mode) {
  const normalized = String(mode ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  if (normalized === "paid_and_organic" || normalized === "paidandorganic") return "paid_and_organic"
  if (normalized === "organic_only" || normalized === "organiconly") return "organic_only"
  if (normalized === "paid_only" || normalized === "paidonly") return "paid_only"
  return null
}

function metricResultEntryToPaidOrganicBucket(entry, bucketKey) {
  if (!entry || typeof entry !== "object") return null
  const camelBucket =
    bucketKey === "paid_and_organic"
      ? entry.paidAndOrganic
      : bucketKey === "organic_only"
        ? entry.organicOnly
        : bucketKey === "paid_only"
          ? entry.paidOnly
          : null
  const nested = bucketKey && (entry[bucketKey] ?? camelBucket)
  const nestedObject = nested && typeof nested === "object" ? nested : null
  const nestedHasValues = Boolean(
    nestedObject &&
      (nestedObject.new_value != null ||
        nestedObject.newValue != null ||
        nestedObject.latest_value != null ||
        nestedObject.latestValue != null ||
        nestedObject.previous_value != null ||
        nestedObject.previousValue != null),
  )
  const source = nestedHasValues ? nestedObject : entry
  return {
    new_value: source.new_value ?? source.newValue ?? null,
    previous_value: source.previous_value ?? source.previousValue ?? null,
    latest_value: source.latest_value ?? source.latestValue ?? null,
    growth: source.growth ?? null,
    goal_progress: source.goal_progress ?? source.goalProgress ?? null,
    yoy_growth: source.yoy_growth ?? source.yoyGrowth ?? null,
    yoy_previous_value: source.yoy_previous_value ?? source.yoyPreviousValue ?? null,
    goal: source.goal ?? null,
    cost_per_result: source.cost_per_result ?? source.costPerResult ?? null,
  }
}

/** Flat `results.{metric}` map → dashboard `metrics.{metric}.paid_and_organic` shape. */
export function pagePerformanceResultsToMetrics(results) {
  const metrics = {}
  if (!results || typeof results !== "object") return metrics

  for (const [metricKey, entry] of Object.entries(results)) {
    const paidOrganic = metricResultEntryToPaidOrganicBucket(entry)
    if (!paidOrganic) continue
    metrics[metricKey] = {
      organic: null,
      paid: null,
      paid_and_organic: paidOrganic,
    }
  }

  return metrics
}

function metricBucketHasValues(bucket) {
  if (!bucket) return false
  return [bucket.new_value, bucket.previous_value, bucket.latest_value, bucket.yoy_previous_value].some(
    (value) => value != null && value !== "" && Number.isFinite(Number(value)),
  )
}

function sumMetricField(left, right) {
  const a = left == null || left === "" || Number.isNaN(Number(left)) ? null : Number(left)
  const b = right == null || right === "" || Number.isNaN(Number(right)) ? null : Number(right)
  if (a == null) return b
  if (b == null || b === 0) return a
  if (a === 0) return b
  return a + b
}

function mergePaidOrganicBuckets(existing, incoming) {
  if (!metricBucketHasValues(existing)) return incoming
  if (!metricBucketHasValues(incoming)) return existing
  return {
    ...existing,
    ...incoming,
    new_value: sumMetricField(existing.new_value, incoming.new_value),
    previous_value: sumMetricField(existing.previous_value, incoming.previous_value),
    latest_value: sumMetricField(existing.latest_value, incoming.latest_value),
    yoy_previous_value: sumMetricField(existing.yoy_previous_value, incoming.yoy_previous_value),
    growth: existing.growth ?? incoming.growth ?? null,
    goal_progress: existing.goal_progress ?? incoming.goal_progress ?? null,
    yoy_growth: existing.yoy_growth ?? incoming.yoy_growth ?? null,
    goal: existing.goal ?? incoming.goal ?? null,
    cost_per_result: existing.cost_per_result ?? incoming.cost_per_result ?? null,
  }
}

function pageResultsMap(page) {
  const hasEntries = (raw) => {
    if (Array.isArray(raw)) return raw.length > 0
    return Boolean(raw && typeof raw === "object" && Object.keys(raw).length > 0)
  }
  const raw = hasEntries(page?.results) ? page.results : page?.metrics
  if (!hasEntries(raw)) return null
  if (Array.isArray(raw)) {
    const map = {}
    for (const entry of raw) {
      if (!entry || typeof entry !== "object") continue
      const key = entry.metric_name ?? entry.metricName ?? entry.name
      if (!key) continue
      map[key] = entry
    }
    return map
  }
  return raw
}

function mergePageResultsIntoMetrics(metrics, results, bucketKey) {
  if (!bucketKey || !results || typeof results !== "object") return metrics
  const out = metrics ?? {}

  for (const [metricKey, entry] of Object.entries(results)) {
    const bucket = metricResultEntryToPaidOrganicBucket(entry, bucketKey)
    if (!bucket) continue

    const existingMetric = out[metricKey] ?? {
      organic_only: null,
      paid_only: null,
      paid_and_organic: null,
    }
    const previous = existingMetric[bucketKey]
    out[metricKey] = {
      ...existingMetric,
      [bucketKey]: mergePaidOrganicBuckets(previous, bucket),
    }
  }

  return out
}

function unwrapClientPageReportRows(data) {
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.data?.data)) return data.data.data
  if (Array.isArray(data?.pages)) return data.pages
  if (Array.isArray(data?.data?.pages)) return data.data.pages
  if (Array.isArray(data?.results)) return data.results
  if (Array.isArray(data)) return data
  return []
}

function pageDateKey(value) {
  const match = String(value ?? "").trim().match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ""
}

function pageRowOverlapsRange(page, startDate, endDate) {
  if (!startDate || !endDate) return true
  const pageStart = pageDateKey(page?.start_date ?? page?.startDate)
  const pageEnd = pageDateKey(page?.end_date ?? page?.endDate)
  // The request is already scoped to the range. Undated rows are that response.
  if (!pageStart || !pageEnd) return true
  const rangeStart = pageDateKey(startDate) || String(startDate)
  const rangeEnd = pageDateKey(endDate) || String(endDate)
  return pageStart <= rangeEnd && pageEnd >= rangeStart
}

function normalizePagesPerformancePayload(data, socialNetworkTypes = [], range = null) {
  const pages = unwrapClientPageReportRows(data)
  if (!pages.length) return []

  const byTypeId = new Map()
  const startDate = range?.startDate
  const endDate = range?.endDate
  const fallbackBucket =
    aggregationModeToMetricsBucketKey(range?.aggregationMode) ?? "paid_and_organic"

  for (const page of pages) {
    if (!pageRowOverlapsRange(page, startDate, endDate)) continue

    const bucketKey =
      aggregationModeToMetricsBucketKey(page?.aggregation_mode ?? page?.aggregationMode) ??
      fallbackBucket

    const networkName = page?.social_network
    const fromName = socialNetworkNameToTypeId(networkName, socialNetworkTypes)
    const directId = Number(page?.social_network_type_id)
    const directOk = !Number.isNaN(directId)
    const directMapsToChannel =
      directOk && Boolean(socialNetworkTypeIdToDashboardPlatformKey(directId, socialNetworkTypes))
    const typeId = isApiAllNetwork(networkName)
      ? 0
      : fromName ?? (directMapsToChannel ? directId : null) ?? (directOk ? directId : null)
    if (typeId == null) continue

    const existing = byTypeId.get(typeId)
    const mergedMetrics = mergePageResultsIntoMetrics(
      existing?.metrics,
      pageResultsMap(page),
      bucketKey,
    )

    byTypeId.set(typeId, {
      social_network_type_id: typeId,
      socialNetworkTypeId: typeId,
      social_network: networkName ?? existing?.social_network ?? null,
      page_name: page?.page_name ?? existing?.page_name ?? null,
      metrics: mergedMetrics,
    })
  }

  return Array.from(byTypeId.values())
}

export function extractPaidOrganicNewValue(metrics, metricKey) {
  return extractPaidOrganicMetric(metrics, metricKey).newValue
}

function parseMetricNum(value) {
  if (value == null || value === "") return null
  const numeric = Number(value)
  return Number.isNaN(numeric) ? null : numeric
}

function paidAndOrganicOnlyEntry(bucket) {
  if (!bucket) return null
  return bucket.paid_and_organic ?? bucket.paidAndOrganic ?? null
}

/** Pulse / comparison / SEO: `paid_and_organic` only (no organic/paid substitute). */
function paidOrganicEntry(bucket) {
  if (!bucket) return null
  return paidAndOrganicOnlyEntry(bucket)
}

export function extractPaidOrganicMetric(metrics, metricKey) {
  const bucket = metrics?.[metricKey]
  const entry = paidOrganicEntry(bucket)
  if (!entry) return { newValue: null, previousValue: null, growth: null, latestValue: null, goalProgress: null }

  return {
    newValue: parseMetricNum(entry.new_value ?? entry.newValue),
    previousValue: parseMetricNum(entry.previous_value ?? entry.previousValue),
    growth: parseMetricNum(entry.growth),
    latestValue: parseMetricNum(entry.latest_value ?? entry.latestValue),
    goalProgress: parseMetricNum(entry.goal_progress ?? entry.goalProgress),
  }
}

export function extractPaidOrganicLatestValue(metrics, metricKey) {
  return extractPaidOrganicMetric(metrics, metricKey).latestValue
}

function formatMetricDisplay(value, isRate) {
  if (value == null || Number.isNaN(Number(value))) {
    return isRate ? formatPercent(0, 1) : formatNumber(0)
  }
  if (isRate) return formatPercent(value, 1)
  return formatNumber(value)
}

function buildRowFromRawValues(typeId, rawValuesByMetricKey, socialNetworkTypes, labelOverride, comparisons) {
  const values = {}

  for (const column of MONTHLY_SOCIAL_COLUMNS) {
    const raw = rawValuesByMetricKey[column.apiMetricKey] ?? null
    values[column.key] = formatMetricDisplay(raw, column.isRate)
  }

  const catalogLabel = socialNetworkTypeIdToLabel(socialNetworkTypes, typeId)
  const label =
    labelOverride ??
    (catalogLabel || (typeId === 0 ? "All Platforms" : ""))

  const row = { label, values, socialNetworkTypeId: typeId }
  if (comparisons) row.comparisons = comparisons
  return row
}

function metricEntryForAggregationSource(metrics, metricKey, aggregationSource) {
  const bucket = metrics?.[metricKey]
  if (!bucket) return null
  const mode =
    TABLE_SOURCE_TO_AGGREGATION_MODE[aggregationSource] ?? TABLE_SOURCE_TO_AGGREGATION_MODE.all
  return bucket[mode] ?? null
}

function platformHasMetricsForSource(platform, aggregationSource) {
  const metrics = platform?.metrics ?? {}
  for (const column of MONTHLY_SOCIAL_COLUMNS) {
    const extracted = extractMetricForAggregationSource(metrics, column.apiMetricKey, aggregationSource)
    if (metricDisplayValueForTable(extracted) != null) return true
  }
  return false
}

export function extractMetricForAggregationSource(metrics, metricKey, aggregationSource = "all") {
  const entry = metricEntryForAggregationSource(metrics, metricKey, aggregationSource)
  if (!entry) {
    return {
      newValue: null,
      previousValue: null,
      latestValue: null,
      growth: null,
      goalProgress: null,
      yoyGrowth: null,
      yoyPreviousValue: null,
    }
  }
  return {
    newValue: parseMetricNum(entry.new_value ?? entry.newValue),
    previousValue: parseMetricNum(entry.previous_value ?? entry.previousValue),
    latestValue: parseMetricNum(entry.latest_value ?? entry.latestValue),
    growth: parseMetricNum(entry.growth),
    goalProgress: parseMetricNum(entry.goal_progress ?? entry.goalProgress),
    yoyGrowth: parseMetricNum(entry.yoy_growth ?? entry.yoyGrowth),
    yoyPreviousValue: parseMetricNum(entry.yoy_previous_value ?? entry.yoyPreviousValue),
  }
}

/** Social Performance table primary cell: period total from `new_value`. */
export function metricDisplayValueForTable(extracted) {
  if (!extracted) return null
  return extracted.newValue
}

/** API `growth` / `yoy_growth` are relative change ratios → display percent points. */
export function growthRatioToComparisonPercent(ratio) {
  if (ratio == null || Number.isNaN(Number(ratio))) return null
  return Number(ratio) * 100
}

function buildRowFromPlatform(platform, socialNetworkTypes, aggregationSource = "all", labelOverride) {
  const typeId = Number(platform?.social_network_type_id ?? platform?.socialNetworkTypeId)
  const metrics = platform?.metrics ?? {}
  const rawValuesByMetricKey = {}
  const comparisonsMom = {}
  const comparisonsYoy = {}

  for (const column of MONTHLY_SOCIAL_COLUMNS) {
    const extracted = extractMetricForAggregationSource(metrics, column.apiMetricKey, aggregationSource)
    rawValuesByMetricKey[column.apiMetricKey] = metricDisplayValueForTable(extracted)
    comparisonsMom[column.key] = growthRatioToComparisonPercent(extracted.growth)
    comparisonsYoy[column.key] = growthRatioToComparisonPercent(extracted.yoyGrowth)
  }

  return buildRowFromRawValues(typeId, rawValuesByMetricKey, socialNetworkTypes, labelOverride, {
    mom: comparisonsMom,
    yoy: comparisonsYoy,
  })
}

function addNullableNumbers(left, right) {
  if (left == null && right == null) return null
  const a = left == null || Number.isNaN(Number(left)) ? 0 : Number(left)
  const b = right == null || Number.isNaN(Number(right)) ? 0 : Number(right)
  return a + b
}

function ratioFromTotals(numerator, denominator) {
  if (numerator == null && denominator == null) return null
  const den = Number(denominator) || 0
  if (den <= 0) return null
  return (Number(numerator) || 0) / den
}

function normalizeRateForAggregate(value) {
  if (value == null || Number.isNaN(Number(value))) return null
  const numeric = Number(value)
  return numeric > 1 ? numeric / 100 : numeric
}

function rateFieldFromExtracted(extracted, field) {
  if (!extracted) return null
  if (field === "previousValue") return extracted.previousValue
  if (field === "yoyPreviousValue") return extracted.yoyPreviousValue
  return extracted.newValue
}

/** Weighted `new_engagement_rate` from connected channels when impressions are 0. */
function weightedApiEngagementRate(platforms, aggregationSource, field) {
  let weighted = 0
  let weightSum = 0

  for (const platform of platforms) {
    const metrics = platform?.metrics ?? {}
    const rate = normalizeRateForAggregate(
      rateFieldFromExtracted(
        extractMetricForAggregationSource(metrics, "new_engagement_rate", aggregationSource),
        field,
      ),
    )
    if (rate == null) continue

    const reach = rateFieldFromExtracted(
      extractMetricForAggregationSource(metrics, "reach", aggregationSource),
      field,
    )
    const engagements = rateFieldFromExtracted(
      extractMetricForAggregationSource(metrics, "new_post_engagement", aggregationSource),
      field,
    )
    const weight = Number(reach) > 0 ? Number(reach) : Number(engagements) > 0 ? Number(engagements) : 1
    weighted += rate * weight
    weightSum += weight
  }

  if (weightSum <= 0) return null
  return weighted / weightSum
}

function allPlatformsEngagementRate(totals, connectedPlatforms, aggregationSource, field) {
  const fromImpressions = ratioFromTotals(
    totals.new_post_engagement?.[field],
    totals.impressions?.[field],
  )
  if (fromImpressions != null) return fromImpressions

  const fromApi = weightedApiEngagementRate(connectedPlatforms, aggregationSource, field)
  if (fromApi != null) return fromApi

  return ratioFromTotals(
    totals.new_post_engagement?.[field],
    totals.reach?.[field],
  )
}

function comparisonFromTotals(current, previous) {
  if (current == null || previous == null) return null
  const curr = Number(current)
  const prev = Number(previous)
  if (!Number.isFinite(curr) || !Number.isFinite(prev) || prev === 0) return null
  return ((curr - prev) / Math.abs(prev)) * 100
}

/** All Platforms = frontend sum of connected channel rows. Empty if none are connected. */
function buildAllPlatformsRowFromConnected(connectedPlatforms, socialNetworkTypes, aggregationSource) {
  const emptyAllValues = Object.fromEntries(
    MONTHLY_SOCIAL_COLUMNS.map((column) => [column.apiMetricKey, null]),
  )
  if (!Array.isArray(connectedPlatforms) || connectedPlatforms.length === 0) {
    return {
      ...buildRowFromRawValues(0, emptyAllValues, socialNetworkTypes),
      empty: true,
    }
  }

  const totals = Object.fromEntries(
    MONTHLY_SOCIAL_COLUMNS.filter((column) => !column.isRate).map((column) => [
      column.apiMetricKey,
      { newValue: null, previousValue: null, yoyPreviousValue: null },
    ]),
  )

  for (const platform of connectedPlatforms) {
    const metrics = platform?.metrics ?? {}
    for (const column of MONTHLY_SOCIAL_COLUMNS) {
      if (column.isRate) continue
      const extracted = extractMetricForAggregationSource(
        metrics,
        column.apiMetricKey,
        aggregationSource,
      )
      const bucket = totals[column.apiMetricKey]
      bucket.newValue = addNullableNumbers(bucket.newValue, extracted.newValue)
      bucket.previousValue = addNullableNumbers(bucket.previousValue, extracted.previousValue)
      bucket.yoyPreviousValue = addNullableNumbers(bucket.yoyPreviousValue, extracted.yoyPreviousValue)
    }
  }

  const rateNew = allPlatformsEngagementRate(
    totals,
    connectedPlatforms,
    aggregationSource,
    "newValue",
  )
  const ratePrev = allPlatformsEngagementRate(
    totals,
    connectedPlatforms,
    aggregationSource,
    "previousValue",
  )
  const rateYoyPrev = allPlatformsEngagementRate(
    totals,
    connectedPlatforms,
    aggregationSource,
    "yoyPreviousValue",
  )

  const rawValuesByMetricKey = {}
  const comparisonsMom = {}
  const comparisonsYoy = {}

  for (const column of MONTHLY_SOCIAL_COLUMNS) {
    if (column.isRate) {
      rawValuesByMetricKey[column.apiMetricKey] = rateNew
      comparisonsMom[column.key] = comparisonFromTotals(rateNew, ratePrev)
      comparisonsYoy[column.key] = comparisonFromTotals(rateNew, rateYoyPrev)
      continue
    }
    const bucket = totals[column.apiMetricKey]
    rawValuesByMetricKey[column.apiMetricKey] = bucket.newValue
    comparisonsMom[column.key] = comparisonFromTotals(bucket.newValue, bucket.previousValue)
    comparisonsYoy[column.key] = comparisonFromTotals(bucket.newValue, bucket.yoyPreviousValue)
  }

  return buildRowFromRawValues(0, rawValuesByMetricKey, socialNetworkTypes, undefined, {
    mom: comparisonsMom,
    yoy: comparisonsYoy,
  })
}

function findPlatformForDashboardKey(byId, list, socialNetworkTypes, platformKey) {
  const typeId = catalogTypeIdFromKey(socialNetworkTypes, platformKey)
  if (typeId != null && byId.has(typeId)) return byId.get(typeId)

  const match = (Array.isArray(list) ? list : []).find((platform) => {
    const id = platformTypeId(platform)
    if (id === 0 || Number.isNaN(id)) return false
    const fromType = socialNetworkTypeIdToDashboardPlatformKey(id, socialNetworkTypes)
    const networkName = platform?.social_network ?? platform?.socialNetwork
    const fromName = dashboardPlatformKeyFromDescription(networkName)
    const instagramName =
      platformKey === "instagram" &&
      (socialNetworkDescription(networkName) === "instagram" ||
        socialNetworkDescription(networkName) === INSTAGRAM_GRAPH_DESCRIPTION)
    return fromType === platformKey || fromName === platformKey || instagramName
  })
  if (match) return match

  return null
}

/** Build monthly social tables for All / Organic / Paid traffic from the same API payload. */
export function buildMonthlySocialTablesBySource(platforms, socialNetworkTypes = [], options = {}) {
  return {
    all: buildMonthlySocialTable(platforms, socialNetworkTypes, { ...options, aggregationSource: "all" }),
    organic: buildMonthlySocialTable(platforms, socialNetworkTypes, { ...options, aggregationSource: "organic" }),
    paid: buildMonthlySocialTable(platforms, socialNetworkTypes, { ...options, aggregationSource: "paid" }),
  }
}

/**
 * Normalize API platforms into Monthly Social Performance table rows.
 * Cell values are `results.[metric].new_value` from the matching aggregation_mode:
 * All → paid_and_organic, Organic → organic_only, Paid → paid_only.
 * Engagements / Engagement Rate use `new_post_engagement` / `new_engagement_rate` only.
 * All Platforms is summed on the frontend from connected channels only.
 * If no channel is connected, All Platforms has no data.
 */
export function buildMonthlySocialTable(platforms, socialNetworkTypes = [], options = {}) {
  const { platformKeysFilter = null, aggregationSource = "all" } = options
  const restrictToKeys = Array.isArray(platformKeysFilter)
  const filterKeys = restrictToKeys
    ? new Set(platformKeysFilter.map(normalizeDashboardPlatformKey))
    : null

  const orderedKeys = DASHBOARD_SOCIAL_PERFORMANCE_PLATFORM_KEYS.filter(
    (key) => !restrictToKeys || filterKeys.has(key),
  )

  const list = Array.isArray(platforms) ? platforms : []
  const byId = new Map()
  for (const platform of list) {
    const id = platformTypeId(platform)
    if (Number.isNaN(id)) continue
    byId.set(id, platform)
  }

  const platformEntries = orderedKeys.map((platformKey) => ({
    platformKey,
    platform: findPlatformForDashboardKey(byId, list, socialNetworkTypes, platformKey),
  }))

  const rows = [
    buildAllPlatformsRowFromConnected(
      platformEntries.map((entry) => entry.platform).filter(Boolean),
      socialNetworkTypes,
      aggregationSource,
    ),
  ]

  for (const { platformKey, platform } of platformEntries) {
    if (!platform && !restrictToKeys) continue
    const label = socialNetworkPrettyLabelForDashboardKey(platformKey)
    rows.push({
      ...buildRowFromPlatform(platform, socialNetworkTypes, aggregationSource, label),
      platform: platformKey,
    })
  }

  return {
    columns: MONTHLY_SOCIAL_COLUMNS.map(({ key, label, info }) =>
      info ? { key, label, info } : { key, label },
    ),
    rows,
  }
}

export function normalizeSocialPerformancePayload(data, socialNetworkTypes = [], range = null) {
  if (isDailyStatusPayload(data)) {
    return normalizeDailyStatusPayload(data)
  }

  return normalizePagesPerformancePayload(data, socialNetworkTypes, range)
}

export function resolveSocialPerformancePlatforms(slice, socialNetworkTypes = []) {
  if (slice?.raw != null) {
    return normalizeSocialPerformancePayload(slice.raw, socialNetworkTypes, {
      startDate: slice.startDate,
      endDate: slice.endDate,
      aggregationMode: aggregationModeFromSocialFetchKey(slice.fetchedFetchKey),
    })
  }
  return Array.isArray(slice?.platforms) ? slice.platforms : []
}

export function resolvePulseSocialPerformancePlatforms(slice, socialNetworkTypes = []) {
  if (slice?.pulseRaw != null) {
    const snapshotDate = slice.pulseStartDate
    return normalizeSocialPerformancePayload(slice.pulseRaw, socialNetworkTypes, {
      startDate: snapshotDate,
      endDate: snapshotDate,
    })
  }
  return Array.isArray(slice?.pulsePlatforms) ? slice.pulsePlatforms : []
}

/** Convert API engagement rate to chart decimal (0–1) for formatPercent. */
export function normalizeEngagementRateValue(value) {
  if (value == null || Number.isNaN(Number(value))) return 0
  const numeric = Number(value)
  return numeric > 1 ? numeric / 100 : numeric
}

/** Format API `growth` (relative change ratio) for tooltip display. */
export function formatSocialPerformanceGrowth(growth) {
  if (growth == null || Number.isNaN(Number(growth))) return null
  return `${Math.abs(Number(growth) * 100).toFixed(1)}%`
}

/**
 * Build horizontal bar chart rows from social performance API platforms.
 * Excludes All Platforms (`social_network_type_id: 0`).
 */
export function buildPlatformPerformanceComparisonData(platforms, options = {}) {
  const {
    platformKeysFilter = null,
    socialNetworkTypes = [],
    aggregationSource = "all",
  } = options
  const restrictToKeys = Array.isArray(platformKeysFilter)
  const filterKeys = restrictToKeys
    ? new Set(platformKeysFilter.map(normalizeDashboardPlatformKey))
    : null

  const orderedKeys = DASHBOARD_SOCIAL_PERFORMANCE_PLATFORM_KEYS.filter(
    (key) => !restrictToKeys || filterKeys.has(key),
  )

  const list = Array.isArray(platforms) ? platforms : []
  const byId = new Map()
  for (const platform of list) {
    const id = platformTypeId(platform)
    if (Number.isNaN(id)) continue
    byId.set(id, platform)
  }

  const rows = orderedKeys.flatMap((platformKey) => {
    const platform = findPlatformForDashboardKey(byId, list, socialNetworkTypes, platformKey)
    if (!platform && !restrictToKeys) return []
    const metrics = platform?.metrics ?? {}
    const engagementRateMetric = extractMetricForAggregationSource(
      metrics,
      "new_engagement_rate",
      aggregationSource,
    )
    const impressionsMetric = extractMetricForAggregationSource(
      metrics,
      "impressions",
      aggregationSource,
    )
    const engagementsMetric = extractMetricForAggregationSource(
      metrics,
      "new_post_engagement",
      aggregationSource,
    )
    const reachMetric = extractMetricForAggregationSource(metrics, "reach", aggregationSource)

    return {
      platform: platformKey,
      engagementRate: normalizeEngagementRateValue(engagementRateMetric.newValue) * 100,
      lifetimeEngagements: engagementsMetric.latestValue ?? 0,
      compare: {
        engagementRate: engagementRateMetric,
        impressions: impressionsMetric,
        engagements: engagementsMetric,
        reach: reachMetric,
      },
    }
  })

  return rows.sort((a, b) => b.engagementRate - a.engagementRate)
}

function trendFromValues(previous, latest) {
  if (latest == null || previous == null) return "stable"
  if (latest > previous) return "up"
  if (latest < previous) return "down"
  return "stable"
}

/** API `social_network: "all"` only — never summed from connected platforms. */
export function resolveSocialPerformanceAggregateMetrics(platforms) {
  const aggregatePlatform = findApiAllPlatform(platforms)
  return aggregatePlatform?.metrics ?? {}
}

function topPlatformIdByMetric(platforms, options = {}) {
  const { platformKeysFilter = null, socialNetworkTypes = [] } = options
  const list = platformKeysFilter?.length
    ? filterSocialPerformancePlatformsByKeys(platforms, platformKeysFilter, socialNetworkTypes)
    : Array.isArray(platforms) ? platforms : []

  let topId = null
  let topValue = -1

  for (const platform of list) {
    const typeId = Number(platform?.social_network_type_id ?? platform?.socialNetworkTypeId)
    if (typeId === 0) continue
    const metric = extractFollowerMetric(platform?.metrics ?? {})
    const value = socialPerformanceMetricDisplayValue(metric)
    if (value > topValue) {
      topValue = value
      topId = typeId
    }
  }

  return topValue > 0 ? topId : null
}

/** Prefer latest totals for pulse breakdown; fall back to period new_value. */
function socialPerformanceMetricDisplayValue(metric) {
  if (!metric) return 0
  if (metric.latestValue != null) return metric.latestValue
  return 0
}

/**
 * Soho Pulse engagement card from `new_post_engagement.new_value`.
 */
export function buildEngagementCardFromSocialPerformance(
  baseCard,
  platforms,
  socialNetworkTypes = [],
  options = {},
) {
  if (!baseCard) return baseCard

  const metrics = resolveSocialPerformanceAggregateMetrics(platforms, {
    ...options,
    socialNetworkTypes,
  })
  const engagement = extractPaidOrganicMetric(metrics, "new_post_engagement")
  const previous = engagement.previousValue ?? 0
  const displayTotal = engagement.newValue ?? 0

  const likes = extractPaidOrganicMetric(metrics, "post_likes")
  const comments = extractPaidOrganicMetric(metrics, "post_comments")
  const shares = extractPaidOrganicMetric(metrics, "post_shares")

  const platformEngagementRows = buildPlatformEngagementRows(
    platforms,
    socialNetworkTypes,
    options,
  )
  const topEngagementRow = platformEngagementRows.find((row) => row.totalEngagements > 0)
  const topPlatform = topEngagementRow?.label ?? "—"

  const pct = engagement.growth != null ? engagement.growth * 100 : 0

  return {
    ...baseCard,
    subLabel: baseCard.subLabel || "Total actions",
    value: formatNumber(displayTotal),
    trophyInsight:
      topPlatform !== "—"
        ? `Most interactions came from ${topPlatform}.`
        : "No platform interactions in this period.",
    platformBreakdown: socialPerformancePlatformsToBreakdown(
      platforms,
      "new_post_engagement",
      "new",
      extractPaidOrganicMetric,
      socialNetworkTypes,
    ),
    sparklineData: [previous, displayTotal],
    sparklineTrend: trendFromValues(previous, displayTotal),
    sparklinePercentChange: pct,
    goalProgress: engagement.goalProgress ?? baseCard.goalProgress,
    platformEngagementRows,
    detailRows: [
      { label: "Likes", value: formatNumber(socialPerformanceMetricDisplayValue(likes)) },
      { label: "Comments", value: formatNumber(socialPerformanceMetricDisplayValue(comments)) },
      { label: "Shares", value: formatNumber(socialPerformanceMetricDisplayValue(shares)) },
    ],
  }
}

/**
 * Soho Pulse follower growth card from daily status API (`followers`).
 */
export function buildGrowthCardFromSocialPerformance(
  baseCard,
  platforms,
  socialNetworkTypes = [],
  options = {},
) {
  if (!baseCard) return baseCard

  const metrics = resolveSocialPerformanceAggregateMetrics(platforms, {
    ...options,
    socialNetworkTypes,
  })
  const followers = extractFollowerMetric(metrics)
  const growth = followers.growth == null ? 0 : Number(followers.growth)
  const growthPct = Number.isFinite(growth) ? growth * 100 : 0
  const latestTotal = followers.latestValue ?? 0
  const netNew = followers.newValue == null ? 0 : Number(followers.newValue)

  const topPlatformId = topPlatformIdByMetric(platforms, { ...options, socialNetworkTypes })
  const topPlatform =
    netNew !== 0 && topPlatformId != null
      ? socialNetworkTypeIdToLabel(socialNetworkTypes, topPlatformId)
      : "—"

  const detailRows = (Array.isArray(platforms) ? platforms : [])
    .filter((platform) => {
      const typeId = platformTypeId(platform)
      return !Number.isNaN(typeId) && typeId !== 0
    })
    .map((platform) => {
      const typeId = platformTypeId(platform)
      const m = extractFollowerMetric(platform?.metrics ?? {})
      const name = socialNetworkTypeIdToLabel(socialNetworkTypes, typeId)
      return { label: name ? `${name} Followers` : "Followers", value: formatNumber(m.latestValue ?? 0) }
    })

  return {
    ...baseCard,
    value: formatNumber(latestTotal),
    secondaryValue: formatFollowerGrowthPercent(growth),
    netGrowthToday: growthPct,
    followerNewValue: netNew,
    trophyInsight:
      netNew !== 0 && topPlatform !== "—"
        ? `${topPlatform} drove the most follower gains.`
        : latestTotal > 0
          ? `${formatNumber(latestTotal)} total followers across connected platforms.`
          : baseCard.trophyInsight,
    platformBreakdown: socialPerformancePlatformsToBreakdown(
      platforms,
      "followers",
      "new",
      extractPaidOrganicMetric,
      socialNetworkTypes,
    ),
    sparklineData: [0, netNew],
    sparklineTrend: trendFromValues(0, netNew),
    sparklinePercentChange: null,
    goalProgress: followers.goalProgress ?? baseCard.goalProgress,
    platformFollowerRows: buildPlatformFollowerRows(platforms, socialNetworkTypes),
    detailRows,
  }
}
